import React, { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Send, Loader2 } from 'lucide-react';

/**
 * InquiryThread — collapsible reply panel for one inquiry row (#15).
 *
 * - Lazily loads the full thread on first expand.
 * - Buyer and seller can both reply; role tag distinguishes speakers.
 * - Enforces the 20/minute reply limit at the API layer; UI just handles
 *   the response and re-fetches.
 */
const InquiryThread = ({ inquiryId, currentUserId, onSent, defaultOpen = false, onOpened }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(false);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const openedOnceRef = useRef(false);

  const loadThread = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/inquiries/${inquiryId}/thread`);
      setThread(data.thread || []);
    } catch { toast.error('Could not load conversation'); }
    finally { setLoading(false); }
  };

  // Best-effort mark-read: fires the first time this conversation is
  // expanded (either manually or via ?open= deep-link). Parent uses the
  // onOpened callback to clear the "New Reply" pill immediately.
  const markThreadRead = React.useCallback(async () => {
    if (openedOnceRef.current) return;
    openedOnceRef.current = true;
    try {
      await api.post(`/inquiries/${inquiryId}/mark-thread-read`);
      onOpened?.();
    } catch { /* silent — badge will clear on next reload */ }
  }, [inquiryId, onOpened]);

  const toggle = async () => {
    if (!open && thread === null) await loadThread();
    setOpen(o => {
      const next = !o;
      if (next) markThreadRead();
      return next;
    });
  };

  // Auto-open path (deep link from a bell notification): load the thread
  // once on mount and mark inbound messages read.
  useEffect(() => {
    if (defaultOpen && thread === null) {
      loadThread();
      markThreadRead();
    }
    // If the parent flips defaultOpen back to true (e.g. bell click to a
    // different inquiry_id while inbox is already mounted), re-expand and
    // clear read state for the newly-selected conversation.
    if (defaultOpen && !open) {
      setOpen(true);
      markThreadRead();
    }
  }, [defaultOpen, thread, markThreadRead, open]);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await api.post(`/inquiries/${inquiryId}/reply`, { body: body.trim() });
      setBody('');
      await loadThread();
      onSent?.();
      toast.success('Message sent');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Could not send message');
      toast.error(msg);
    } finally { setSending(false); }
  };

  return (
    <div className='mt-3 pt-3 border-t' data-testid={`inquiry-thread-${inquiryId}`}>
      <button
        type='button'
        className='text-xs font-medium text-primary hover:underline'
        onClick={toggle}
        data-testid={`inquiry-thread-toggle-${inquiryId}`}
      >
        {open ? 'Hide conversation' : 'Open conversation'}
      </button>
      {open && (
        <div className='mt-3 space-y-3'>
          {loading ? (
            <div className='text-xs text-muted-foreground flex items-center gap-1'>
              <Loader2 className='h-3 w-3 animate-spin' /> Loading…
            </div>
          ) : (
            <div className='space-y-2 max-h-72 overflow-y-auto pr-1'>
              {(thread || []).map(m => {
                const mine = m.from_id === currentUserId;
                return (
                  <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${mine ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}>
                      <div className='text-[10px] uppercase tracking-wide opacity-70 mb-0.5'>
                        {m.from_role} · {new Date(m.sent_at).toLocaleString()}
                      </div>
                      <div className='whitespace-pre-wrap break-words'>{m.body}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className='flex items-end gap-2'>
            <Textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder='Type your reply…'
              className='min-h-[70px] flex-1'
              data-testid={`inquiry-thread-input-${inquiryId}`}
            />
            <Button
              size='sm'
              onClick={send}
              disabled={sending || !body.trim()}
              data-testid={`inquiry-thread-send-${inquiryId}`}
            >
              {sending ? <Loader2 className='h-3.5 w-3.5 animate-spin' /> : <Send className='h-3.5 w-3.5' />}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InquiryThread;
